import { bignumber } from 'mathjs';
import { DECIMAL_PLACES } from 'common/config/app';
import type { WaterfallDecimalInputs } from './waterfallDecimalInputs.types';

const DECIMAL_FIELDS = [
  'exit_equity_value',
  'exit_enterprise_value',
  'plus_cash',
  'less_debt',
  'foreign_exchange_rate',
] as const;
const ROUND_HALF_UP = 4;
const MAX_DECIMAL_INPUT_LENGTH = 1000;
const DECIMAL_LITERAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;

const normalizeDecimal = (value: string): string => {
  if (value.length > MAX_DECIMAL_INPUT_LENGTH || !DECIMAL_LITERAL.test(value.trim())) {
    return value;
  }
  const [mantissa, exponent = '0'] = value.trim().split(/[eE]/);
  const decimalPlaces = (mantissa.split('.')[1]?.length ?? 0) - Number(exponent);
  if (decimalPlaces <= DECIMAL_PLACES) {
    return value;
  }
  try {
    const decimal = bignumber(value.trim());
    if (!decimal.isFinite()) {
      return value;
    }
    // Round decimal strings, not binary floats; already-supported inputs stay untouched.
    const rounded = decimal.toFixed(DECIMAL_PLACES, ROUND_HALF_UP).replace(/\.?0+$/, '');
    return rounded === '-0' ? '0' : rounded;
  } catch {
    // Leave malformed inputs for the existing backend validation rather than replacing them.
    return value;
  }
};

export const normalizeWaterfallDecimalInputs = <T extends WaterfallDecimalInputs>(inputs: T): T => {
  const normalized: WaterfallDecimalInputs = {};
  DECIMAL_FIELDS.forEach(field => {
    const value = inputs[field];
    if (typeof value === 'string') {
      normalized[field] = normalizeDecimal(value);
    }
  });
  if (inputs.related_entity_adjustments != null) {
    normalized.related_entity_adjustments = Object.fromEntries(
      Object.entries(inputs.related_entity_adjustments).map(([id, adjustment]) => [
        id,
        {
          ...adjustment,
          ...(typeof adjustment.plus_cash === 'string' && { plus_cash: normalizeDecimal(adjustment.plus_cash) }),
          ...(typeof adjustment.less_debt === 'string' && { less_debt: normalizeDecimal(adjustment.less_debt) }),
        },
      ])
    );
  }
  return { ...inputs, ...normalized };
};
